import React from "react";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Divider,
  FormControlLabel,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import HttpsOutlinedIcon from "@mui/icons-material/HttpsOutlined";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import PhoneAndroidOutlinedIcon from "@mui/icons-material/PhoneAndroidOutlined";
import ShieldOutlinedIcon from "@mui/icons-material/ShieldOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import VisibilityOffOutlinedIcon from "@mui/icons-material/VisibilityOffOutlined";
import LoginReset from "./LoginReset";

export type LoginStep = "CREDENTIALS" | "OTP_SELECT" | "OTP_VERIFY";
export type OtpChannel = "EMAIL" | "SMS";

export interface MFALoginFormProps {
  step: LoginStep;
  email: string;
  password: string;
  otp: string;
  otpChannel: OtpChannel;
  loading?: boolean;
  resendCountdown?: number;
  passwordErrors?: string[];
  regulatoryAccepted?: boolean;
  showPassword?: boolean;
  onEmailChange: (value: string) => void;
  onPasswordChange: (value: string) => void;
  onOtpChange: (value: string) => void;
  onOtpChannelChange: (value: OtpChannel) => void;
  onTogglePassword: () => void;
  onCredentialSubmit: () => void;
  onSendOtp: () => void;
  onVerifyOtp: () => void;
  onBackToCredentials: () => void;
  onRegulatoryAcceptedChange: (value: boolean) => void;
  onForgotPassword?: () => void;
}

const primaryButtonSx = {
  height: 48,
  borderRadius: "8px",
  textTransform: "none",
  fontWeight: 700,
  boxShadow: "none",
};

const secondaryButtonSx = {
  height: 46,
  borderRadius: "8px",
  textTransform: "none",
  fontWeight: 700,
};

function StepHeader({
  title,
  subtitle,
}: {
  title: string;
  subtitle: string;
}) {
  return (
    <Stack spacing={0.5} sx={{ mb: 0.5 }}>
      <Typography
        sx={{
          fontSize: 28,
          lineHeight: 1.2,
          fontWeight: 800,
          color: "#303433",
          letterSpacing: "-0.02em",
        }}
      >
        {title}
      </Typography>

      <Typography
        sx={{
          fontSize: 14,
          color: "#66788A",
          lineHeight: 1.6,
        }}
      >
        {subtitle}
      </Typography>
    </Stack>
  );
}

function StepIndicator({ step }: { step: LoginStep }) {
  const items: Array<{ key: LoginStep; label: string }> = [
    { key: "CREDENTIALS", label: "Login credentials" },
    { key: "OTP_SELECT", label: "Channel" },
    { key: "OTP_VERIFY", label: "Verification" },
  ];

  const activeIndex = items.findIndex((item) => item.key === step);

  return (
    <Stack direction="row" spacing={1} sx={{ width: "100%" }}>
      {items.map((item, index) => {
        const active = index <= activeIndex;
        return (
          <Box key={item.key} sx={{ flex: 1 }}>
            <Typography
              sx={{
                fontSize: 11,
                mb: 0.75,
                color: active ? "#B84B00" : "#737978",
                fontWeight: active ? 700 : 600,
              }}
            >
              {item.label}
            </Typography>
            <Box
              sx={{
                height: 6,
                borderRadius: "8px",
                bgcolor: active ? "#B84B00" : "#F0F1F0",
                transition: "all .2s ease",
              }}
            />
          </Box>
        );
      })}
    </Stack>
  );
}

function SecurityNote() {
  return (""
    // <Alert
    //   icon={<ShieldOutlinedIcon fontSize="inherit" />}
    //   severity="success"
    //   sx={{
    //     borderRadius: "8px",
    //     bgcolor: "#F3FBF6",
    //     color: "#184B30",
    //     border: "1px solid #D7EFDF",
    //     "& .MuiAlert-icon": {
    //       color: "#B84B00",
    //     },
    //   }}
    // >
    //   Access is restricted to authorized personnel. All sign-ins are logged.
    // </Alert>
  );
}

export default function MFALoginForm(props: any) {
  const [resetOpen, setResetOpen] = React.useState(false);
  const {
    step,
    email,
    password,
    otp,
    otpChannel,
    loading = false,
    resendCountdown = 0,
    passwordErrors = [],
    showPassword = false,
    onEmailChange,
    onPasswordChange,
    onOtpChange,
    onOtpChannelChange,
    onTogglePassword,
    onCredentialSubmit,
    onSendOtp,
    onVerifyOtp,
    onBackToCredentials,
    onRegulatoryAcceptedChange,
    onForgotPassword,
  } = props;

  const showNotification = (message: string, type: string, title: string) => {
    if (typeof props.onMessage === "function") {
      props.onMessage(type || "info", title || "Reset Password", message);
      return;
    }
    console.log(title || "Reset Password", type || "info", message);
  };

  const handleForgotPassword = () => {
    setResetOpen(true);
    if (typeof onForgotPassword === "function") onForgotPassword();
  };

  return (
    <Stack spacing={2.5}>
      <StepIndicator step={step} />
      <SecurityNote />

      {step === "CREDENTIALS" && (
        <>
          <LoginReset
            open={resetOpen}
            onClose={() => setResetOpen(false)}
            showNotification={showNotification}
          />

          {/* <StepHeader
            title="Secure sign in"
            subtitle="Access the SCSMS portal with your institutional credentials."
          /> */}

          {/* <Alert
            severity="info"
            sx={{
              borderRadius: "8px",
              bgcolor: "#F7FAFC",
              border: "1px solid #E3EAF0",
              color: "#415A6B",
              "& .MuiAlert-icon": {
                color: "#B84B00",
              },
            }}
          > 
            By accessing this portal, you acknowledge the system access, security,
            and confidentiality requirements.
          </Alert>*/}

          <TextField
            fullWidth
            label="Username"
            value={email}
            onChange={(e) => onEmailChange(e.target.value)}
            autoComplete="username"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <EmailOutlinedIcon sx={{ color: "#737978" }} />
                </InputAdornment>
              ),
            }}
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: "8px",
              },
            }}
          />

          <TextField
            fullWidth
            label="Password"
            type={showPassword ? "text" : "password"}
            value={password}
            onChange={(e) => onPasswordChange(e.target.value)}
            autoComplete="current-password"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <LockOutlinedIcon sx={{ color: "#737978" }} />
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={onTogglePassword} edge="end">
                    {showPassword ? <VisibilityOffOutlinedIcon /> : <VisibilityOutlinedIcon />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: "8px",
              },
            }}
          />

          {passwordErrors.length > 0 && (
            <Box
              sx={{
                p: 2,
                borderRadius: "8px",
                bgcolor: "#FFF8F7",
                border: "1px solid #F6D5D1",
              }}
            >
              <Typography sx={{ fontSize: 12, fontWeight: 800, color: "#9B2C1D", mb: 0.5 }}>
                The password does not yet meet the required policy:
              </Typography>
              {passwordErrors.map((err:any) => (
                <Typography key={err} sx={{ fontSize: 12, color: "#9B2C1D", lineHeight: 1.5 }}>
                  • {err}
                </Typography>
              ))}
            </Box>
          )}

          <Stack
            direction={{ xs: "column", sm: "row" }}
            justifyContent="space-between"
            alignItems={{ xs: "flex-start", sm: "center" }}
            spacing={1}
          >
            <Button
              variant="text"
              onClick={handleForgotPassword}
              sx={{ textTransform: "none", fontWeight: 700, color: "#B84B00", px: 0.5 }}
            >
              Forgot password?
            </Button>

            <Typography sx={{ fontSize: 12, color: "#7B8B98" }}>
              Multi-factor authentication enabled
            </Typography>
          </Stack>

          <Button
            fullWidth
            variant="contained"
            disabled={!email || !password || passwordErrors.length > 0 || loading}
            onClick={onCredentialSubmit}
            sx={{
              ...primaryButtonSx,
              bgcolor: "#B84B00",
              "&:hover": { bgcolor: "#923A00" },
            }}
          >
            {loading ? <CircularProgress size={22} sx={{ color: "#fff" }} /> : "Continue"}
          </Button>
        </>
      )}

      {step === "OTP_SELECT" && (
        <>
          {/* <StepHeader
            title="Choose a verification channel"
            subtitle="Select the channel where you want to receive your one-time code."
          /> */}
          <Typography sx={{ fontSize: 13, color: "#66788A" }}>
            Choose a verification channel.
          </Typography>

          <Stack spacing={1.5}>
            <Button
              fullWidth
              variant={otpChannel === "EMAIL" ? "contained" : "outlined"}
              onClick={() => onOtpChannelChange("EMAIL")}
              startIcon={<EmailOutlinedIcon />}
              sx={{
                ...secondaryButtonSx,
                justifyContent: "flex-start",
                bgcolor: otpChannel === "EMAIL" ? "#B84B00" : undefined,
                borderColor: "#C9CCCB",
                color: otpChannel === "EMAIL" ? "#fff" : "#303433",
              }}
            >
              Receive the code by email
            </Button>

            <Button
              fullWidth
              variant={otpChannel === "SMS" ? "contained" : "outlined"}
              onClick={() => onOtpChannelChange("SMS")}
              startIcon={<PhoneAndroidOutlinedIcon />}
              sx={{
                ...secondaryButtonSx,
                justifyContent: "flex-start",
                bgcolor: otpChannel === "SMS" ? "#B84B00" : undefined,
                borderColor: "#C9CCCB",
                color: otpChannel === "SMS" ? "#fff" : "#303433",
              }}
            >
              Receive the code by SMS
            </Button>
          </Stack>

          <Divider />

          <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
            <Button
              fullWidth
              variant="outlined"
              onClick={onBackToCredentials}
              sx={{ ...secondaryButtonSx, borderColor: "#C9CCCB", color: "#303433" }}
            >
              Back
            </Button>
            <Button
              fullWidth
              variant="contained"
              onClick={onSendOtp}
              sx={{
                ...primaryButtonSx,
                bgcolor: "#B84B00",
                "&:hover": { bgcolor: "#923A00" },
              }}
            >
              {loading ? <CircularProgress size={22} sx={{ color: "#fff" }} /> : "Send code"}
            </Button>
          </Stack>
        </>
      )}

      {step === "OTP_VERIFY" && (
        <>
          {/* <StepHeader
            title="Code verification"
            subtitle={`Enter the code received by ${otpChannel === "EMAIL" ? "email" : "SMS"} to complete sign-in.`}
          /> */}
          <Typography sx={{ fontSize: 13, color: "#66788A" }}>
            Enter the code you received to complete sign-in.
          </Typography>

          <TextField
            fullWidth
            label="Verification code"
            value={otp}
            onChange={(e) => onOtpChange(e.target.value)}
            inputProps={{ maxLength: 8, inputMode: "numeric" }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <HttpsOutlinedIcon sx={{ color: "#737978" }} />
                </InputAdornment>
              ),
            }}
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: "8px",
              },
            }}
          />

          <Stack
            direction={{ xs: "column", sm: "row" }}
            justifyContent="space-between"
            spacing={1}
          >
            <Button
              variant="text"
              onClick={onSendOtp}
              disabled={loading || resendCountdown > 0}
              sx={{ textTransform: "none", fontWeight: 700, color: "#B84B00", px: 0.5 }}
            >
              {resendCountdown > 0
                ? `Resend in ${resendCountdown}s`
                : "Resend code"}
            </Button>

            <Button
              variant="text"
              onClick={onBackToCredentials}
              sx={{ textTransform: "none", fontWeight: 700, color: "#303433", px: 0.5 }}
            >
              Change login credentials
            </Button>
          </Stack>

          <Button
            fullWidth
            variant="contained"
            disabled={!otp || loading}
            onClick={onVerifyOtp}
            sx={{
              ...primaryButtonSx,
              bgcolor: "#B84B00",
              "&:hover": { bgcolor: "#923A00" },
            }}
          >
            {loading ? <CircularProgress size={22} sx={{ color: "#fff" }} /> : "Verify and sign in"}
          </Button>
        </>
      )}

      <Box
        sx={{
          pt: 1,
          borderTop: "1px solid #EEF2F5",
        }}
      >
        <Typography sx={{ fontSize: 12, color: "#737978", lineHeight: 1.7 }}>
          Technical assistance · portal support · secure sign in
        </Typography>
      </Box>
    </Stack>
  );
}
